
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PageLoading } from '@/components/shared/PageLoading';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'client' | 'therapist' | 'admin';
  redirectTo?: string;
}

export const ProtectedRoute = ({ 
  children, 
  requiredRole,
  redirectTo = '/auth/login' 
}: ProtectedRouteProps) => {
  const { user, loading, rolesLoaded, isClient, isTherapist, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate(redirectTo);
      return;
    }

    if (!loading && user && requiredRole && rolesLoaded) {
      const hasRequiredRole = 
        (requiredRole === 'client' && isClient) ||
        (requiredRole === 'therapist' && isTherapist) ||
        (requiredRole === 'admin' && isAdmin);

      if (!hasRequiredRole) {
        navigate('/unauthorized');
        return;
      }
    }
  }, [user, loading, rolesLoaded, requiredRole, navigate, redirectTo, isClient, isTherapist, isAdmin]);

  if (loading) {
    return <PageLoading />;
  }

  if (!user) {
    return null;
  }

  // Wait for roles to load if a specific role is required
  if (requiredRole && !rolesLoaded) {
    return <PageLoading />;
  }

  if (requiredRole && rolesLoaded) {
    const hasRequiredRole = 
      (requiredRole === 'client' && isClient) ||
      (requiredRole === 'therapist' && isTherapist) ||
      (requiredRole === 'admin' && isAdmin);

    if (!hasRequiredRole) {
      return null;
    }
  }

  return <>{children}</>;
};
